import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface SubscriptionPlan {
  id: string;
  name: string;
  price_monthly: number;
  stripe_price_id?: string;
  features: {
    monthly_free_sessions?: number;
    referral_rate?: number;
    max_groups?: number;
    advanced_analytics?: boolean;
    branding_features?: boolean;
    priority_support?: boolean;
    community_access?: boolean;
    [key: string]: any; // Allow additional dynamic properties
  };
  is_active: boolean;
}

export interface PractitionerSubscription {
  id: string;
  practitioner_id: string;
  plan_id: string;
  stripe_subscription_id?: string;
  status: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  plan_name: string;
  features: SubscriptionPlan['features'];
}

export const usePractitionerSubscription = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch current subscription
  const { data: subscription, isLoading, error } = useQuery({
    queryKey: ['practitioner-subscription', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await backendClient
        .from('practitioner_subscriptions')
        .select(`
          status,
          current_period_end,
          subscription_tiers!inner (
            tier_name,
            features
          )
        `)
        .eq('practitioner_id', user.id)
        .single();

      if (error) {
        // If no subscription found, return null (not an error)
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      if (data) {
        return {
          plan_name: data.subscription_tiers.tier_name,
          features: data.subscription_tiers.features,
          status: data.status,
          current_period_end: data.current_period_end
        };
      }

      return null;
    },
    enabled: !!user,
  });

  // Fetch all available plans (practitioner tiers only)
  const { data: plans = [] } = useQuery({
    queryKey: ['subscription-plans', 'practitioner'],
    queryFn: async () => {
      // Use backend API instead of Supabase
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${backendUrl}/pricing/tiers?user_type=practitioner`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription tiers');
      }

      const data = await response.json();

      // Map to expected interface
      return (data || []).map((tier: any) => ({
        id: tier.id,
        name: tier.tier_name,
        price_monthly: tier.monthly_price,
        stripe_price_id: tier.stripe_price_id,
        features: tier.features || {},
        is_active: tier.is_active
      })) as SubscriptionPlan[];
    },
  });

  // Check feature access
  const hasFeatureAccess = (feature: keyof SubscriptionPlan['features']): boolean => {
    if (!subscription) return false;
    return subscription.features[feature] === true || subscription.features[feature] > 0;
  };

  // Get feature value
  const getFeatureValue = (feature: keyof SubscriptionPlan['features']): any => {
    if (!subscription) return 0;
    return subscription.features[feature] || 0;
  };

  // Check if user is on Pro plan
  const isProPlan = subscription?.plan_name === 'Pro';
  const isBasicPlan = !subscription || subscription?.plan_name === 'Basic';

  // Upgrade subscription mutation
  const upgradeMutation = useMutation({
    mutationFn: async (tierId: string) => {
      if (!user) throw new Error('User not authenticated');

      // This would integrate with Stripe to create subscription
      // For now, just update the database
      const { data: tier } = await backendClient
        .from('subscription_tiers')
        .select('*')
        .eq('id', tierId)
        .single();

      if (!tier) throw new Error('Tier not found');

      // Check if subscription exists, then update or insert
      const { data: existingSub } = await backendClient
        .from('practitioner_subscriptions')
        .select('id')
        .eq('practitioner_id', user.id)
        .maybeSingle();

      if (existingSub) {
        // Update existing subscription
        const { error } = await backendClient
          .from('practitioner_subscriptions')
          .update({
            tier_id: tierId,
            status: 'active',
          })
          .eq('practitioner_id', user.id);

        if (error) throw error;
      } else {
        // Insert new subscription
        const { error } = await backendClient
          .from('practitioner_subscriptions')
          .insert({
            practitioner_id: user.id,
            tier_id: tierId,
            status: 'active',
          });

        if (error) throw error;
      }

      // Update user credits tier
      await backendClient
        .from('user_credits')
        .update({ subscription_tier: tier.tier_name })
        .eq('user_id', user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practitioner-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['practitioner-credits'] });
      toast({
        title: 'Subscription Updated',
        description: 'Your subscription has been successfully updated.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Subscription Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    subscription,
    plans,
    isLoading,
    error,
    hasFeatureAccess,
    getFeatureValue,
    isProPlan,
    isBasicPlan,
    upgradeMutation,
    upgradeSubscription: upgradeMutation.mutate,
    isUpgrading: upgradeMutation.isPending,
  };
};
